import imageCompression from 'browser-image-compression';
// Self-host the worker script via Vite (?url) so importScripts loads same-origin,
// not cdn.jsdelivr.net. If CSP is added later, script-src must allow blob: for the worker.
import compressionLibUrl from 'browser-image-compression/dist/browser-image-compression.js?url';

export type ImageCompressionPreset = 'logo' | 'banner' | 'gallery' | 'lesson' | 'program' | 'play_event';

const PRESETS: Record<
  ImageCompressionPreset,
  { maxWidthOrHeight: number; maxSizeMB: number; initialQuality: number }
> = {
  logo: { maxWidthOrHeight: 512, maxSizeMB: 0.3, initialQuality: 0.85 },
  banner: { maxWidthOrHeight: 1920, maxSizeMB: 0.6, initialQuality: 0.82 },
  gallery: { maxWidthOrHeight: 1600, maxSizeMB: 0.5, initialQuality: 0.8 },
  lesson: { maxWidthOrHeight: 1280, maxSizeMB: 0.4, initialQuality: 0.8 },
  program: { maxWidthOrHeight: 1280, maxSizeMB: 0.4, initialQuality: 0.8 },
  play_event: { maxWidthOrHeight: 1280, maxSizeMB: 0.4, initialQuality: 0.8 },
};

const SKIP_COMPRESSION_TYPES = new Set(['image/svg+xml', 'image/gif']);

const MIME_TO_EXTENSION: Record<string, string> = {
  'image/webp': 'webp',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/svg+xml': 'svg',
};

/** Map File.type to a storage path extension (never derived from file.name). */
export function extensionFromMimeType(mimeType: string): string {
  return MIME_TO_EXTENSION[mimeType] ?? 'jpg';
}

export interface CompressImageResult {
  file: File;
  ext: string;
}

/**
 * Compress raster images to WebP before upload. SVG/GIF are passed through unchanged.
 * On worker or compression failure, returns the original file (upload still succeeds).
 */
export async function compressImage(
  file: File,
  preset: ImageCompressionPreset
): Promise<CompressImageResult> {
  const originalExt = extensionFromMimeType(file.type);

  if (SKIP_COMPRESSION_TYPES.has(file.type)) {
    return { file, ext: originalExt };
  }

  const presetOptions = PRESETS[preset];

  try {
    const compressed = await imageCompression(file, {
      ...presetOptions,
      fileType: 'image/webp',
      useWebWorker: true,
      libURL: compressionLibUrl,
    });

    return {
      file: compressed,
      ext: extensionFromMimeType(compressed.type),
    };
  } catch (error) {
    console.warn('Image compression failed, uploading original file:', error);
    return { file, ext: originalExt };
  }
}
